import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  CircularProgress
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import axios from "axios";
import CustomTableWithPagination from "../../components/customTable/CustomTable";
import {
  pointsDetailsSubtitleStyle,
  pointsDetailsTitleStyle
} from "../../components/styles/commonStyles";
import { getFormattedDate } from "../../Utils/commonFunction";

interface MemberCreditsDetailsPageProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  merchantId: string;
  establishmentName: string;
}

interface CreditDetail {
  PatronId: number;
  MerchantId: number;
  EstablishmentName: string;
  Credit: number;
  Amount?: number; // Mapped from Credit for table column
  DateCreated: string;
  SourceName: string;
}

const apiEndpoint: any = process.env.REACT_APP_API_BASE_URL;

const MemberCreditsDetailsPage: React.FC<MemberCreditsDetailsPageProps> = ({
  open,
  onClose,
  userId,
  merchantId,
  establishmentName
}) => {
  const [loading, setLoading] = useState(false);
  const [creditDetails, setCreditDetails] = useState<CreditDetail[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);

  const fetchCreditDetails = useCallback(
    (page: number = pageNumber, size: number = pageSize) => {
      if (!userId || !merchantId) return;

      setLoading(true);

      const token = localStorage.getItem("businessAccessToken");
      const url = `${apiEndpoint}/loyaltyprogram/businessportal/credits/user/${userId}/business/${merchantId}?PageSize=${size}&PageNumber=${page}`;

      axios
        .get(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        })
        .then((response) => {
          const resData = response?.data || {};
          const mappedData = (resData.Data || []).map((item: any) => ({
            PatronId: item.PatronId,
            MerchantId: item.MerchantId,
            EstablishmentName: item.EstablishmentName,
            Credit: item.Amount || item.Credit,
            Amount: item.Amount || item.Credit, // Map Amount from API to Amount for table column
            DateCreated: item.DateCreated,
            SourceName: item.SourceName
          }));
          setCreditDetails(mappedData);
          // Use TotalCount from API, or fallback to data array length if TotalCount is 0 but data exists
          const apiTotalCount = resData?.TotalCount || resData?.totalCount || 0;
          setTotalCount(apiTotalCount > 0 ? apiTotalCount : mappedData.length);
          if (typeof resData?.PageNumber === "number") {
            setPageNumber(resData.PageNumber);
          } else if (typeof resData?.pageNumber === "number") {
            setPageNumber(resData.pageNumber);
          } else {
            setPageNumber(page);
          }
          if (typeof resData?.PageSize === "number") {
            setPageSize(resData.PageSize);
          } else if (typeof resData?.pageSize === "number") {
            setPageSize(resData.pageSize);
          } else {
            setPageSize(size);
          }
        })
        .catch((error) => {
          console.error("Error fetching credit details:", error);
          setCreditDetails([]);
          setTotalCount(0);
        })
        .finally(() => setLoading(false));
    },
    [userId, merchantId, pageNumber, pageSize]
  );

  useEffect(() => {
    if (open) {
      fetchCreditDetails();
    }
  }, [open, fetchCreditDetails]);

  const handleClose = () => {
    onClose();
  };

  const handleRefresh = () => {
    fetchCreditDetails(pageNumber, pageSize);
  };

  const handleChangeRowsPerPage = (val: any) => {
    const size = Number(val?.target?.value ?? val) || 10;
    setPageSize(size);
    setPageNumber(1);
    fetchCreditDetails(1, size);
  };

  const handleChangePage = (e: any, val: any) => {
    const page = Math.max(1, (typeof val === 'number' ? val : 0) + 1); // Convert from 0-based (UI) to 1-based (API)
    setPageNumber(page);
    fetchCreditDetails(page, pageSize);
  };

  const columns = [
    {
      id: "EstablishmentName",
      label: "Establishment Name",
      minWidth: 200,
      align: "left",
      format: (value: any) => value ?? "-"
    },
    {
      id: "Amount",
      label: "Credit",
      minWidth: 100,
      align: "left",
      format: (value: any) => (value ? `$${value}` : "-")
    },
    {
      id: "DateCreated",
      label: "Created At",
      minWidth: 180,
      align: "left",
      format: (value: any) => (value ? getFormattedDate(value) : "-")
    },
    {
      id: "SourceName",
      label: "Source Name",
      minWidth: 120,
      align: "left",
      format: (value: any) => value ?? "-"
    }
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: "80vh",
          maxHeight: "90vh"
        }
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <Box>
          <Typography variant="h5" component="div" sx={pointsDetailsTitleStyle}>
            Credit Details
          </Typography>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={pointsDetailsSubtitleStyle}
          >
            {establishmentName}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton
            onClick={handleRefresh}
            disabled={loading}
            size="small"
            title="Refresh"
          >
            {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              color: (theme) => theme.palette.grey[500]
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <CustomTableWithPagination
          columns={columns}
          data={creditDetails}
          apiStatus={loading ? "fetching" : "idle"}
          payloadSize={pageSize}
          currentPage={pageNumber - 1}
          totalCount={totalCount}
          lastEvaluatedKey={null}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          handleChangePage={handleChangePage}
          isShowPagination={true}
          noDataFoundMessage="No credit details found."
        />
      </DialogContent>
    </Dialog>
  );
};

export default MemberCreditsDetailsPage;

